/**
 * RunningView — "I am working; you can leave."
 *
 * Story 26739. Visual contract: artboard (2) in
 * `tasks/design/step-views.html`.
 *
 * Three lines and a countdown. What is happening, that it is safe to go, and
 * a held-able wait before the pane closes itself.
 *
 * EVERYTHING BUT THE SECTION SENTENCE IS A CLAIM ABOUT A RUNNING RUN — the
 * "Running" pill, "we'll call you when we need you", the countdown — and the
 * host has to know the run is running before this view makes any of them.
 * `stepViewFor` answers "running" for a null status too (the "not loaded yet"
 * sentinel maps to the active family), so every drawer opens on this view for
 * the moment before its first status read lands — including a drawer on a run
 * that finished last week. That moment may say "Checking where this run is",
 * which is true; it must not wear a "Running" pill or promise a call, which
 * for a finished run are false. Hosts pass `loading` until the status has
 * arrived and the view says only what it can back: the identity line and the
 * section sentence.
 *
 * DELIBERATELY ABSENT: any counter. No "Step 24 of 45", no queue position, no
 * progress bar. This view reports VALUE — what is happening and that it is
 * safe to leave — never position in a list the reader cannot see. The one
 * number on the pane is the countdown, and it counts down to a close rather
 * than down a list of steps (design decisions D4 / D9).
 *
 * THE AUTO-CLOSE IS NOT AN ANSWER. `AnnouncedCountdown`'s action kind is
 * `"close"`; there is no `"submit"` in that union and no path from this
 * component to a resume. Dismissing a view on a run that needs nothing is a
 * courtesy; answering a question because a person did not get back in time
 * would be an unattributable decision.
 *
 * IT DISMISSES THIS VIEW, NOT THE TASK PANE. The copy below says "back to your
 * queue" because that is exactly what the host does. A PINNED PANE MUST NOT BE
 * CLOSED — the Servicer pinned it so it would survive moving between messages,
 * and Office.js offers no way to ask whether they did, so the host never
 * reaches for `Office.addin.hide()` on this countdown's behalf. Wording this
 * as "closing" would promise something that no longer happens, which is the
 * one thing an announced countdown may not do (story 26740).
 *
 * The host owns the timer for the same reason it always did: shared code
 * imports no Office.js (design Constraint 20) — `stepViewsPurity.test.ts`
 * proves it.
 *
 * `onHoldAutoClose` is the escape hatch and it is permanent for this instance:
 * there is no re-arm (design decision D5). `autoCloseHeld` is the SAME hold
 * arriving from the host instead of from the button — what the Servicer is
 * already doing elsewhere on the pane (attaching a document to this run) is as
 * good a reason to stop the clock as pressing Hold, and a better one, because
 * it does not ask them to watch for a countdown while they work.
 */
import type { ReactNode } from "react";
import { StepViewFrame, identityLine } from "./StepViewFrame";
import { AnnouncedCountdown } from "./AnnouncedCountdown";

/** The exact wording on the countdown row. `{seconds}` is substituted live. */
export const AUTO_CLOSE_LABEL =
  "Back to your queue in {seconds}s — nothing is lost, ora will email you";

/** What a screen reader hears once, when the countdown starts. */
export const AUTO_CLOSE_ANNOUNCEMENT =
  "This returns to your queue shortly. Nothing is lost — ora will email you when it needs you.";

/** The second line: the promise that makes leaving safe. */
export const SAFE_TO_CLOSE_COPY =
  "Safe to close — we'll call you when we need you.";

export interface RunningViewProps {
  clientName?: string | null;
  policyNumber?: string | null;
  /**
   * The one sentence saying where the run is — `sectionLabelFor(model, status)`.
   * Never a number.
   */
  sectionLabel?: ReactNode;
  /**
   * True until the run's status has arrived. While loading, the view renders
   * no "Running" pill, no safe-to-close promise and no countdown — only the
   * identity line and the section sentence, which the host has already set to
   * "Checking where this run is" (see the header). Hosts derive it from the
   * same null-status sentinel that dispatched here in the first place.
   */
  loading?: boolean;
  /**
   * Override the safe-to-close line. Defaults to `SAFE_TO_CLOSE_COPY`; `null`
   * renders no line at all.
   */
  safeToCloseCopy?: ReactNode;
  /**
   * Auto-close budget in seconds. `null` renders no countdown at all — for a
   * host that has nothing to close (ora's drawer is not a task pane).
   */
  autoCloseSeconds?: number | null;
  /** Fired once when the countdown expires. Never fired after a hold. */
  onAutoClose?: () => void;
  /** Fired when the Servicer holds. Permanent for this instance. */
  onHoldAutoClose?: () => void;
  /**
   * The host has held the auto-close — by the Hold button below, or because the
   * Servicer started something the close would interrupt (attaching a document
   * to this run, say).
   *
   * The row STAYS and says it is held. A countdown that simply vanishes reads
   * as a glitch, and "this is not going to close on you" is the only thing
   * worth saying to somebody who has just picked up a file.
   */
  autoCloseHeld?: boolean;
  children?: ReactNode;
}

export function RunningView({
  clientName,
  policyNumber,
  sectionLabel,
  loading = false,
  safeToCloseCopy = SAFE_TO_CLOSE_COPY,
  autoCloseSeconds = 10,
  autoCloseHeld = false,
  onAutoClose,
  onHoldAutoClose,
  children,
}: RunningViewProps) {
  return (
    <StepViewFrame
      view="running"
      eyebrow={loading ? null : "Running"}
      tone="quiet"
      headline={identityLine(clientName, policyNumber)}
      subline={sectionLabel}
    >
      {!loading && safeToCloseCopy != null ? (
        <p
          className="text-[12.5px] leading-relaxed text-primary-gray-light-700"
          data-testid="running-safe-to-close"
        >
          {safeToCloseCopy}
        </p>
      ) : null}

      {children}

      {!loading && (autoCloseHeld || autoCloseSeconds != null) ? (
        <AnnouncedCountdown
          // A held countdown has no budget left to state — a host that holds
          // clears the deadline the count was derived from — and the component
          // renders the held row rather than a number, so the zero is never
          // read. It is NOT a one-second budget: `held` suppresses the wait.
          seconds={autoCloseSeconds ?? 0}
          held={autoCloseHeld}
          action={{ kind: "close", label: AUTO_CLOSE_LABEL }}
          announcement={AUTO_CLOSE_ANNOUNCEMENT}
          onFire={onAutoClose}
          onHold={onHoldAutoClose}
        />
      ) : null}
    </StepViewFrame>
  );
}

export default RunningView;
